export interface App {
  id: string
  name: string
  slug: string
  description: string
  iconUrl: string
  iframeUrl: string
  type?: "official" | "private" | "public" | string | null
  status?: string
  clientId?: string
  client_id?: string
  clientSecret?: string
  client_secret?: string
  developerId?: string
  developer_id?: string
  accounts?: string[]
  scopes?: string[]
  samlIssuer?: string | null
  samlAttributes?: any
  category?: string | null
  isCustom?: boolean
  version?: string | null
  environment?: "Alpha" | "Beta" | "Produção" | string | null
}

export interface InstalledApp {
  id: string
  app: App
  configuration: string
  status: string
  sortOrder: number
}

export interface Widget {
  id: string
  name: string
  slug: string
  description?: string | null
  icon: string
  type: string
  dimension: "small" | "large"
  developerType?: "iframe" | "code"
  iframeUrl?: string | null
  code?: string | null
  defaultWidth: number
  defaultHeight: number
  isDefault?: boolean
  status?: string
}

export interface InstalledWidget {
  id: string
  workspaceId: string
  widgetId: string
  widget: Widget
  enabled: boolean
  visible: boolean
  configuration?: string
  status: string
  sortOrder: number
}

export interface Workspace {
  id: string
  name: string
  slug: string
  status?: boolean
  sortOrder: number
  accountId?: string
  createdAt?: string
  updatedAt?: string
  installedApps?: InstalledApp[]
  installedWidgets?: InstalledWidget[]
}

/**
 * Retorna o workspace ativo mais antigo que o usuário tem acesso dentro de uma empresa.
 * Critérios:
 * 1. Deve estar ativo (status !== false)
 * 2. Menor data de criação (createdAt mais antigo / menor timestamp)
 * 3. Fallback: menor sortOrder
 */
export function getOldestActiveWorkspace(workspaces?: Workspace[] | null): Workspace | null {
  if (!workspaces || !Array.isArray(workspaces) || workspaces.length === 0) return null
  const active = workspaces.filter((w) => w && w.status !== false)
  if (active.length === 0) return null

  return [...active].sort((a, b) => {
    if (a.createdAt && b.createdAt) {
      const timeA = new Date(a.createdAt).getTime()
      const timeB = new Date(b.createdAt).getTime()
      if (!isNaN(timeA) && !isNaN(timeB) && timeA !== timeB) {
        return timeA - timeB // Menor timestamp = mais antigo
      }
    }
    if (a.createdAt && !b.createdAt) return -1
    if (!a.createdAt && b.createdAt) return 1

    if (typeof a.sortOrder === "number" && typeof b.sortOrder === "number" && a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder
    }
    return 0
  })[0] || null
}

export interface Account {
  id: string
  name: string
  slug: string
  status?: string
  logoUrl?: string | null
  bannerUrl?: string | null
  document?: string | null
  bio?: string | null
  category?: string | null
  location?: string | null
  website?: string | null
  installedAppIds?: string[]
  workspaces: Workspace[]
}

export interface UserProfile {
  id: string
  name: string
  email: string
  phone?: string | null
  phoneVerifiedAt?: string | null
  termsAcceptedAt?: string | null
  avatarUrl?: string
  twoFactorEnabled: boolean
  loginMusicUrl?: string
  loginMusicEnabled?: boolean
  fullSizeScreen?: boolean
  lockscreenPin?: string | null
}

export interface TabGroup {
  id: string
  name: string
  color: string
  collapsed?: boolean
}

export interface OpenAppTab {
  instanceId: string
  app: App
  baseIframeUrl?: string
  pinned: boolean
  groupId: string | null
  subpath?: string
}

export interface HistoryRecord {
  id: string
  accountId?: string | null
  workspaceId?: string | null
  entityType: string
  entityId: string
  action: "INSERT" | "UPDATE" | "DELETE" | string
  performedBy?: string | null
  changes?: string | null
  oldData?: string | null
  newData?: string | null
  ipAddress?: string | null
  userAgent?: string | null
  createdAt: string
  user?: {
    id: string
    name: string
    email: string
    avatarUrl?: string | null
  } | null
  workspace?: {
    id: string
    name: string
    slug: string
  } | null
}

export interface HistoryResponse {
  items: HistoryRecord[]
  totalCount: number
  page: number
  limit: number
  totalPages: number
  hasMore: boolean
}

export type Language = "pt" | "en" | "es"

